import React, { useState } from 'react';

import {
  SectionRoot,
  SectionHeader,
  SectionTitle,
  SectionHeaderRight,
  ChevronIcon,
  SectionBody,
  SectionBodyInner,
} from './styles';

/**
 * CollapsibleSection — card com header clicável e body colapsável.
 *
 * Anatomia do header (esquerda → direita):
 *   título  |  [headerAction slot]  |  chevron (toggle)
 *
 * Colapsado: apenas o header é visível.
 * Expandido: children aparecem abaixo do header.
 *
 * O `headerAction` é um slot genérico — use para qualquer ação secundária
 * (ex.: botão pílula "Onde verifico o nível do meu gás?"), independente do
 * toggle de colapso.
 *
 * Pode ser controlado (`expanded`/`onToggle`) ou não-controlado
 * (`defaultExpanded`).
 *
 * @example
 * <CollapsibleSection
 *   title="Nível do gás"
 *   headerAction={
 *     <Button variant="secondary" shape="pill">
 *       Onde verifico o nível do meu gás?
 *     </Button>
 *   }
 *   defaultExpanded
 * >
 *   <GasLevelForm />
 * </CollapsibleSection>
 */
export type CollapsibleSectionProps = {
  /** Título do header (sempre visível) */
  title: React.ReactNode;
  /**
   * Slot de ação no header (ex.: botão pílula de ajuda). Independente do toggle.
   * Importante: clique no headerAction NÃO propaga para o toggle — use
   * `e.stopPropagation()` internamente se o slot precisar de um botão.
   */
  headerAction?: React.ReactNode;
  /** Body colapsável */
  children?: React.ReactNode;
  /** Estado inicial (não-controlado) @default true */
  defaultExpanded?: boolean;
  /** Estado controlado */
  expanded?: boolean;
  /** Callback de toggle (controlado) */
  onToggle?: (expanded: boolean) => void;
  /** Label acessível do botão de toggle @default 'Recolher/Expandir seção' */
  toggleLabel?: string;
  className?: string;
  style?: React.CSSProperties;
};

const ChevronUp = (): React.ReactElement => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M5 12.5L10 7.5L15 12.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CollapsibleSection = ({
  title,
  headerAction,
  children,
  defaultExpanded = true,
  expanded: expandedProp,
  onToggle,
  toggleLabel,
  className,
  style,
}: CollapsibleSectionProps): React.ReactElement => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>): void => {
    // Não propaga quando o clique veio do headerAction (o slot já tem stopPropagation)
    const next = !expanded;
    if (!isControlled) setInternalExpanded(next);
    if (onToggle) onToggle(next);
  };

  const label =
    toggleLabel || (expanded ? 'Recolher seção' : 'Expandir seção');

  return (
    <SectionRoot className={className} style={style}>
      <SectionHeader
        type="button"
        $expanded={expanded}
        aria-expanded={expanded}
        aria-label={label}
        onClick={handleToggle}
      >
        <SectionTitle>{title}</SectionTitle>

        <SectionHeaderRight>
          {headerAction && (
            <span
              role="none"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {headerAction}
            </span>
          )}
          <ChevronIcon $expanded={expanded}>
            <ChevronUp />
          </ChevronIcon>
        </SectionHeaderRight>
      </SectionHeader>

      <SectionBody $expanded={expanded} aria-hidden={!expanded}>
        <SectionBodyInner>
          {children}
        </SectionBodyInner>
      </SectionBody>
    </SectionRoot>
  );
};

export default CollapsibleSection;
